import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { categoryService, creatorStorefrontService } from '../services/api';
import ProductCard from '../components/ProductCard';
import SiteLoading from '../components/SiteLoading';
import {
  groupCreatorProductsByCategory,
  normalizeCreatorProductList,
  readCreatorStorefrontFromStorage,
  resolveCreatorDisplayName,
} from '../utils/creatorStorefront';
import {
  buildCanonicalCreatorShopPath,
  dispatchStorefrontSegmentMode,
  isMongoObjectIdString,
  normalizeCreatorSlugInput,
  rememberCreatorSlug,
} from '../utils/creatorSlug';
import './CategoryPage.css';
import './CreatorProductsPage.css';

function mergeCreatorProfile(creatorIdentifier, apiCreator, products) {
  const canonicalId =
    apiCreator?._id || apiCreator?.id || creatorIdentifier;
  const stored = readCreatorStorefrontFromStorage(canonicalId);
  const sample = products.find((p) => p?.createdBy);
  const nameFromProducts = sample ? resolveCreatorDisplayName(sample.createdBy) : null;

  const name =
    apiCreator?.name ||
    apiCreator?.displayName ||
    stored?.name ||
    nameFromProducts ||
    'Creator';

  const role = apiCreator?.role || sample?.createdBy?.role || stored?.role || 'team';
  const displayName = role === 'admin' ? 'Super Wear' : name;

  return {
    id: canonicalId,
    creatorSlug:
      normalizeCreatorSlugInput(apiCreator?.creatorSlug) ||
      normalizeCreatorSlugInput(stored?.creatorSlug) ||
      '',
    name: displayName,
    role,
    bio: apiCreator?.bio || apiCreator?.creatorBio || stored?.bio || '',
    headline: apiCreator?.creatorHeadline || stored?.headline || '',
    bannerUrl:
      apiCreator?.creatorBannerUrl ||
      apiCreator?.bannerUrl ||
      stored?.bannerUrl ||
      apiCreator?.avatar ||
      stored?.avatar ||
      '',
    avatarUrl: apiCreator?.avatar || stored?.avatar || '',
  };
}

const CreatorProductsPage = ({ legacyCreatorId = '' } = {}) => {
  const params = useParams();
  const identifierRaw = legacyCreatorId || params.creatorId || '';
  const creatorIdentifier = identifierRaw
    ? decodeURIComponent(String(identifierRaw))
    : '';

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [creatorIdentifier]);

  useEffect(() => {
    if (creatorIdentifier) {
      dispatchStorefrontSegmentMode('shop', creatorIdentifier);
    }
  }, [creatorIdentifier]);

  useEffect(() => {
    if (!creatorIdentifier) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [profileRes, productList, categoriesRes] = await Promise.all([
          creatorStorefrontService.getCreatorProfile(creatorIdentifier),
          creatorStorefrontService.fetchAllProductsByCreator(creatorIdentifier),
          categoryService.getCategories().catch(() => ({ data: [] })),
        ]);

        if (cancelled) return;

        const normalized = normalizeCreatorProductList(productList);
        setProducts(normalized);

        const cats = Array.isArray(categoriesRes?.data)
          ? categoriesRes.data
          : Array.isArray(categoriesRes?.categories)
            ? categoriesRes.categories
            : Array.isArray(categoriesRes)
              ? categoriesRes
              : [];
        setCategories(cats);

        const profile = mergeCreatorProfile(
          creatorIdentifier,
          profileRes?.creator || null,
          normalized
        );
        if (profile.id && profile.creatorSlug) {
          rememberCreatorSlug(profile.id, profile.creatorSlug);
        }
        setCreatorProfile(profile);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load creator products');
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [creatorIdentifier]);

  const groupedSections = useMemo(
    () => groupCreatorProductsByCategory(products, categories),
    [products, categories]
  );

  const displayName = creatorProfile?.name || 'Creator';
  const bannerUrl = creatorProfile?.bannerUrl || '';
  const hasBanner = Boolean(bannerUrl);

  const canonicalShopPath = creatorProfile
    ? buildCanonicalCreatorShopPath({
        _id: creatorProfile.id,
        creatorSlug: creatorProfile.creatorSlug,
      })
    : null;
  const shouldRedirectToCanonicalSlug =
    !loading &&
    canonicalShopPath &&
    isMongoObjectIdString(creatorIdentifier);

  if (shouldRedirectToCanonicalSlug) {
    return <Navigate to={canonicalShopPath} replace />;
  }

  if (!creatorIdentifier) {
    return (
      <div className="category-page creator-products-page">
        <div className="creator-products-inner">
          <p className="creator-products-empty">Invalid creator link.</p>
          <Link to="/categories" className="creator-products-back">
            Browse categories
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="category-page creator-products-page">
      <header
        className={`creator-products-hero${hasBanner ? ' creator-products-hero--banner' : ''}`}
      >
        {hasBanner ? (
          <div className="creator-products-banner-wrap">
            <img
              src={bannerUrl}
              alt=""
              className="creator-products-banner-img"
            />
          </div>
        ) : null}
        <div className="creator-products-hero-body">
          <div className="creator-products-hero-inner">
            <nav className="creator-products-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true"> / </span>
              <span>Created by {displayName}</span>
            </nav>
            <div className="creator-products-hero-head">
              {creatorProfile?.avatarUrl && !hasBanner ? (
                <img
                  src={creatorProfile.avatarUrl}
                  alt=""
                  className="creator-products-avatar"
                />
              ) : null}
              <div>
                <h1 className="creator-products-title">Products by {displayName}</h1>
                {creatorProfile?.headline ? (
                  <p className="creator-products-headline">{creatorProfile.headline}</p>
                ) : null}
                <p className="creator-products-sub">
                  {loading
                    ? 'Loading…'
                    : `${products.length} product${products.length === 1 ? '' : 's'}`}
                </p>
                {creatorProfile?.bio ? (
                  <p className="creator-products-bio">{creatorProfile.bio}</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="creator-products-inner">
        {loading && <SiteLoading />}

        {!loading && error && (
          <p className="creator-products-error" role="alert">
            {error}
          </p>
        )}

        {!loading && !error && products.length === 0 && (
          <p className="creator-products-empty">No products are listed for this creator yet.</p>
        )}

        {!loading && !error && groupedSections.length > 0 && (
          <div className="creator-products-sections">
            {groupedSections.map((section) => (
              <section
                key={section.key}
                className="creator-products-category-section"
                aria-labelledby={`creator-cat-${section.key}`}
              >
                <div className="creator-products-section-header">
                  <h2 id={`creator-cat-${section.key}`} className="creator-products-section-title">
                    {section.mainCategoryName}
                  </h2>
                  {section.subcategoryName ? (
                    <p className="creator-products-section-sub">
                      Subcategory: {section.subcategoryName}
                    </p>
                  ) : null}
                  <span className="creator-products-section-count">
                    {section.products.length} item{section.products.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="creator-products-grid">
                  {section.products.map((product) => {
                    const id = product.id || product._id;
                    return (
                      <ProductCard
                        key={id}
                        product={{ ...product, id, _id: product._id || product.id }}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorProductsPage;
